import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Package, Search, ShoppingCart, Plus, Minus, Trash2, Pencil, List as ListIcon, LayoutGrid } from "lucide-react";
import HeroImageUpload from "@/components/HeroImageUpload";

const CATEGORIES = ["all", "motor", "propeller", "battery", "esc", "frame", "camera", "misc"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CART_KEY = "droneops_cart";

export default function PartsPage() {
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState(() => { try { return JSON.parse(sessionStorage.getItem(CART_KEY)) || {}; } catch { return {}; } });
  const [pview, setPview] = useState("list"); // list | tiles
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/parts", { params: { q: q || undefined, category: cat } });
    setParts(r.data);
  }, [q, cat]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { sessionStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  // Handle Stripe return
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      if (tries++ > 6) { toast.error("Payment status check timed out"); return; }
      try {
        const r = await api.get(`/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Payment successful! Order placed.");
          setCart({}); sessionStorage.removeItem(CART_KEY);
          setParams({}, { replace: true });
        } else if (r.data.status === "expired") {
          toast.error("Payment session expired");
          setParams({}, { replace: true });
        } else { setTimeout(poll, 2000); }
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partMap = Object.fromEntries(parts.map((p) => [p.id, p]));
  const cartEntries = Object.entries(cart).filter(([id]) => partMap[id] || true);
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartTotal = Object.entries(cart).reduce((sum, [id, qty]) => sum + (partMap[id]?.price || 0) * qty, 0);

  const addToCart = (p) => { setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 })); toast.success(`${p.name} added`); };
  const setQty = (id, qty) => setCart((c) => { const n = { ...c }; if (qty <= 0) delete n[id]; else n[id] = qty; return n; });

  const imgUrl = (path) => `${BACKEND}/api/files/${path}`;

  const deletePart = async (p) => { try { await api.delete(`/parts/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const checkout = () => {
    if (cartCount === 0) return;
    navigate("/cart");
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Spare Parts Store</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <Package className="h-8 w-8 text-lime-500" /> Parts
          </h1>
          <p className="text-slate-500 mt-1">Search, manage and buy drone spare parts.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="manage-part-btn" onClick={() => navigate("/parts/new")}><Plus className="h-4 w-4 mr-1.5" /> Add Part</Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button data-testid="open-cart-btn" className="bg-slate-900 hover:bg-slate-800 text-white relative">
                <ShoppingCart className="h-4 w-4 mr-1.5" /> Cart
                {cartCount > 0 && <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-lime-400 text-slate-900 text-xs font-bold flex items-center justify-center">{cartCount}</span>}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md flex flex-col">
              <SheetHeader><SheetTitle className="font-heading">Your Cart</SheetTitle></SheetHeader>
              <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="cart-items">
                {cartCount === 0 ? <p className="text-sm text-slate-400 text-center py-10">Your cart is empty.</p> :
                  Object.entries(cart).map(([id, qty]) => {
                    const p = partMap[id]; if (!p) return null;
                    return (
                      <div key={id} className="flex items-center gap-3 border-b border-slate-100 pb-3">
                        <div className="h-12 w-12 rounded bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                          {p.image_path ? <img src={imgUrl(p.image_path)} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                          <p className="text-xs text-slate-500">${p.price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setQty(id, qty - 1)} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                          <span className="text-sm w-5 text-center">{qty}</span>
                          <button onClick={() => setQty(id, qty + 1)} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                          <button onClick={() => setQty(id, 0)} className="ml-1 text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between font-heading font-semibold text-lg mb-3"><span>Total</span><span data-testid="cart-total">${cartTotal.toFixed(2)} AUD</span></div>
                <Button data-testid="checkout-btn" onClick={checkout} disabled={cartCount === 0} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">
                  Proceed to checkout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="parts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts, SKU, compatibility…" className="pl-9" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-40" data-testid="parts-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="parts-view-toggle">
          <button data-testid="parts-view-list-btn" onClick={() => setPview("list")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${pview === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><ListIcon className="h-4 w-4" /> List</button>
          <button data-testid="parts-view-tiles-btn" onClick={() => setPview("tiles")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${pview === "tiles" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><LayoutGrid className="h-4 w-4" /> Tiles</button>
        </div>
      </div>

      {pview === "tiles" ? (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="parts-grid">
        {parts.map((p) => (
          <div key={p.id} data-testid={`part-card-${p.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-all flex flex-col">
            <div className="h-40 bg-slate-100 flex items-center justify-center overflow-hidden">
              {p.image_path ? <img src={imgUrl(p.image_path)} alt={p.name} className="h-full w-full object-cover" /> : (
                <HeroImageUpload testid={`part-hero-${p.id}`} label="Add photo" rounded="rounded-none" className="h-full w-full"
                  imagePath={null}
                  onUploaded={async (path) => { await api.put(`/parts/${p.id}`, { ...p, image_path: path }); load(); }} />
              )}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-lime-600">{p.category}</span>
              <h3 className="font-heading font-semibold text-slate-900 mt-0.5 leading-tight hover:text-lime-600 cursor-pointer" data-testid={`part-name-${p.id}`} onClick={() => navigate(`/parts/${p.id}`)}>{p.name}</h3>
              {p.sku && <p className="text-xs text-slate-400 mt-0.5">SKU: {p.sku}</p>}
              {p.compatible_with && <p className="text-xs text-slate-500 mt-1 line-clamp-1">Fits: {p.compatible_with}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-lg font-bold font-heading text-slate-900">${p.price.toFixed(2)}</span>
                <span className={`text-xs font-medium ${p.stock > 5 ? "text-lime-600" : p.stock > 0 ? "text-amber-600" : "text-red-600"}`}>{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" data-testid={`add-cart-${p.id}`} onClick={() => addToCart(p)} disabled={p.stock <= 0} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"><ShoppingCart className="h-3.5 w-3.5 mr-1" /> Add</Button>
                <Button size="sm" variant="outline" data-testid={`edit-part-${p.id}`} onClick={() => navigate(`/parts/${p.id}/edit`)} className="relative" title="Edit part & sub-parts">
                  <Pencil className="h-3.5 w-3.5" />
                  {p.sub_parts?.length > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-lime-400 text-slate-900 text-[10px] font-bold flex items-center justify-center">{p.sub_parts.length}</span>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => deletePart(p)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
      <div className="space-y-2" data-testid="parts-list">
        {parts.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No parts found.</p>}
        {parts.map((p) => (
          <div key={p.id} data-testid={`part-row-${p.id}`} className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:shadow-md hover:border-slate-300 transition-all">
            <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
              {p.image_path ? <img src={imgUrl(p.image_path)} alt={p.name} className="h-full w-full object-cover" /> : <Package className="h-6 w-6 text-slate-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading font-semibold text-slate-900 truncate hover:text-lime-600 cursor-pointer" data-testid={`part-name-${p.id}`} onClick={() => navigate(`/parts/${p.id}`)}>{p.name}</h3>
              <p className="text-xs text-slate-500 truncate">{p.category}{p.sku ? ` · SKU ${p.sku}` : ""}{p.compatible_with ? ` · fits ${p.compatible_with}` : ""}</p>
            </div>
            <span className="hidden sm:block text-sm font-bold font-heading text-slate-900 shrink-0">${p.price.toFixed(2)}</span>
            <span className={`hidden md:block text-xs font-medium shrink-0 w-24 text-right ${p.stock > 5 ? "text-lime-600" : p.stock > 0 ? "text-amber-600" : "text-red-600"}`}>{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" data-testid={`add-cart-${p.id}`} onClick={() => addToCart(p)} disabled={p.stock <= 0} className="bg-slate-900 hover:bg-slate-800 text-white"><ShoppingCart className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 relative text-slate-400 hover:text-slate-900" data-testid={`edit-part-${p.id}`} onClick={() => navigate(`/parts/${p.id}/edit`)} title="Edit part & sub-parts">
                <Pencil className="h-4 w-4" />
                {p.sub_parts?.length > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-lime-400 text-slate-900 text-[10px] font-bold flex items-center justify-center">{p.sub_parts.length}</span>}
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => deletePart(p)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
